<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :maxBlocksInCache="null"
        >
            <b-button-group>
                <properties-search-multiselect
                    v-model="propertiesSelected"
                    variant="primary"
                    :properties="properties"
                ></properties-search-multiselect>
            </b-button-group>
            <responsive-button
                :icon="['fas', 'arrows-rotate']"
                @click="refresh"
                variant="primary"
                class="ml-1"
                >Refresh</responsive-button
            >
              <responsive-button
                :icon="['fas', 'plus']"
                variant="primary"
                @click="handleCreateRecurringRentalExpenseClick"
                >Add Recurring Rental Expense</responsive-button
            >
        </grid>
        <recurring-rental-expense-details-modal-form
            :modal-id="recurringRentalExpenseCreateModalID"
            :key="recurringRentalExpenseCreateModalKey"
            :create="createRecurringRentalExpense"
            @success-save="refresh"
        />
        <recurring-rental-expense-details-modal-form
            :modal-id="recurringRentalExpenseUpdateModalID"
            :initial-values="currentRecurringRentalExpenseInitialValues"
            :update="updateRecurringRentalExpense"
            @success-save="refresh"
        />
    </page>
</template>

<script>
import API from '../../../api';
import { escapeHTML } from '../../../utils/strings';
import Grid from '../../generic/grid/grid.vue';
import Modal from '../../generic/modal/modal.vue';
import Page from '../../generic/page/page.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import PropertiesSearchMultiselect from '../../generic/properties-search-multiselect/properties-search-multiselect.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import RecurringRentalExpenseDetailsModalForm from './recurring-rental-expense-details-modal-form.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';

export default {
    name: 'RecurringRentalExpenseList',
    components: {
        Grid,
        Modal,
        Page,
        ResponsiveButton,
        PropertiesSearchMultiselect,
        RecurringRentalExpenseDetailsModalForm,
        actionsCellRenderer: ActionsCellRenderer,
    },
    mixins: [removeScrollMixin],
    mounted() {
        (async () => {
            this.busy = true;
            try {
                await this.fetchProperties();
            } finally {
                this.busy = false;
            }
        })();
    },
    computed: {
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(
                    '/app/rental-expense/recurring-rental-expense/grid/',
                    {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            properties_and_rooms:
                                this.propertiesAndRoomsRequest,
                        },
                    }
                );
            };
        },
        propertiesAndRoomsRequest() {
            const propertiesAndRooms = new Set();
            this.propertiesSelected.forEach((property) => {
                propertiesAndRooms.add(property.property_id);
                if (!!property.room_id) {
                    propertiesAndRooms.add(-property.room_id);
                }
            });
            return [...propertiesAndRooms].join(',');
        },
        currentRecurringRentalExpenseInitialValues() {
            if (!!this.currentRecurringRentalExpense) {
                return {
                    id: this.currentRecurringRentalExpense.id,
                    amount: this.currentRecurringRentalExpense.amount,
                    account_id: this.currentRecurringRentalExpense.account_id,
                    vendor_id: this.currentRecurringRentalExpense.vendor_id,
                    assignee: this.currentRecurringRentalExpense.assignee,
                    description: this.currentRecurringRentalExpense.description,
                    recurrence: this.currentRecurringRentalExpense.recurrence,
                    properties:
                        this.currentRecurringRentalExpense.properties.map(
                            (property) => ({
                                property_id: property.property_id,
                                room_id: property.room_id,
                            })
                        ),
                };
            }
        },
    },
    data() {
        return {
            busy: false,
            tabs: [
                {
                    title: 'One-Time',
                    route: '/app/rental-expense/list/',
                    active: false,
                },
                {
                    title: 'Recurring',
                    route: '/app/rental-expense/recurring/list/',
                    active: true,
                },
            ],
            recurringRentalExpenseCreateModalID:
                'recurring-rental-expense-create-modal',
            recurringRentalExpenseCreateModalKey: 0,
            recurringRentalExpenseUpdateModalID:
                'recurring-rental-expense-update-modal',
            currentRecurringRentalExpense: null,
            properties: [],
            propertiesSelected: [],
            columnDefs: [
                {
                    field: 'id',
                    type: 'idColumn',
                    maxWidth: 110,
                },
                {
                    field: 'recurrence.freq',
                    headerName: 'Frequency',
                },
                {
                    field: 'recurrence.start',
                    type: 'dateColumn',
                    headerName: 'Start Date',
                    minWidth: 120,
                },
                {
                    field: 'recurrence.until',
                    type: 'dateColumn',
                    headerName: 'Expire Date',
                    minWidth: 120,
                },
                {
                    field: 'properties',
                    headerName: 'Properties',
                    autoHeight: true,
                    minWidth: 320,
                    cellRenderer: ({ data }) =>
                        data &&
                        data.properties
                            .map((property) => escapeHTML(property.name))
                            .join('<br />'),
                },
                {
                    field: 'description',
                    headerName: 'Description',
                },
                {
                    field: 'amount',
                    headerName: 'Amount',
                },
                {
                    field: 'assignee_name',
                    headerName: 'Assignee',
                },
                {
                    headerName: '',
                    maxWidth: 130,
                    minWidth: 130,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        update: (recurringRentalExpense) => {
                            this.currentRecurringRentalExpense =
                                recurringRentalExpense;
                            this.$bvModal.show(
                                this.recurringRentalExpenseUpdateModalID
                            );
                        },
                        delete: async (recurringRentalExpense) => {
                            try {
                                await API.delete(
                                    `/app/rental-expense/recurring-rental-expense/${recurringRentalExpense.id}/`
                                );
                            } catch (error) {
                                this.errorToast({
                                    text: 'Could not delete recurring rental expense.',
                                });
                                return;
                            }
                            this.refresh();
                        },
                    },
                },
            ],
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
        };
    },
    methods: {
        async fetchProperties() {
            try {
                const response = await API.get(
                    `/app/rental-expense/rental-expense/properties/`
                );
                this.properties = response.data;
            } catch {
                this.errorToast({ text: 'Could not load properties.' });
            }
        },
        handleCreateRecurringRentalExpenseClick() {
            this.recurringRentalExpenseCreateModalKey += 1;
            this.$nextTick(() => {
                this.$bvModal.show(this.recurringRentalExpenseCreateModalID);
            });
        },
        createRecurringRentalExpense(payload) {
            return API.post(
                `/app/rental-expense/recurring-rental-expense/`,
                payload
            );
        },
        updateRecurringRentalExpense({ id, ...payload }) {
            return API.patch(
                `/app/rental-expense/recurring-rental-expense/${id}/`,
                payload
            );
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
    },
    watch: {
        propertiesSelected() {
            this.refresh();
        },
    },
};
</script>